// REACT IMPORTS: Core React functionality and hooks
import React, { useState, useEffect, useRef } from "react";

// ASSETS: App logo image
import logo from '../Images/Logo.png';

// ROUTING: React Router for navigation
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

// AUTHENTICATION: Auth context and components
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './auth/AuthModal';
import TierUpgradeModal from './TierUpgradeModal';
import SubscriptionManagementModal from './SubscriptionManagementModal';
import PaymentIssueModal from './PaymentIssueModal';
import { Button } from './ui/button';
import { isPaymentIssueInGrace } from '../lib/billingGrace';

// STYLING: Now using Tailwind CSS classes instead of traditional CSS
// All nav styling has been converted to Tailwind utility classes
const NAV_SECONDARY_ACTION_CLASS = "inline-flex h-11 items-center gap-2 rounded-full border border-brand bg-gradient-to-r from-orange-500 to-orange-600 px-5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-glow"
const NAV_PASSIVE_GREETING_CLASS = "inline-flex h-11 items-center rounded-full border border-brand bg-white px-5 text-sm font-medium text-brand shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:rounded-2xl hover:border-0 hover:bg-gradient-to-r hover:from-orange-500 hover:to-orange-600 hover:text-white hover:shadow-glow"
const NAV_UPGRADE_ACTION_CLASS = "h-11 rounded-lg border border-yellow-400 bg-yellow-400 px-5 text-sm font-semibold text-yellow-900 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:rounded-2xl hover:border-0 hover:bg-gradient-to-r hover:from-orange-500 hover:to-orange-600 hover:text-white hover:shadow-glow"
const MOBILE_MENU_ITEM_CLASS = "flex w-full items-center justify-between border-b border-gray-100 px-4 py-3 text-left text-sm font-medium text-warm-charcoal transition-colors duration-200 last:border-b-0 hover:bg-orange-50 hover:text-brand"
const MOBILE_MENU_PRIMARY_CLASS = "mx-4 mb-3.5 mt-2.5 flex w-[calc(100%-2rem)] items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-sm font-semibold text-white shadow-modern transition-all duration-200 hover:from-orange-600 hover:to-orange-700"
const MOBILE_MENU_PANEL_CLASS = "absolute inset-x-0 top-full z-[120] border-b border-gray-100 bg-white shadow-modern-xl md:hidden"
const MOBILE_MENU_BACKDROP_CLASS = "fixed inset-0 z-[110] bg-black/20 md:hidden"
const MOBILE_MENU_AUTO_OPEN_PATHS = ['/terms', '/privacy', '/support']
const MOBILE_MENU_AUTO_OPEN_STORAGE_PREFIX = 'yummtime-mobile-menu-auto-open:'
const MOBILE_BREAKPOINT_PX = 768

const shouldAutoOpenMobileMenu = (pathname) => {
  if (typeof window === 'undefined') return false
  if (!MOBILE_MENU_AUTO_OPEN_PATHS.includes(pathname)) return false
  if (window.innerWidth >= MOBILE_BREAKPOINT_PX) return false

  try {
    return sessionStorage.getItem(`${MOBILE_MENU_AUTO_OPEN_STORAGE_PREFIX}${pathname}`) !== '1'
  } catch {
    return true
  }
}

const markMobileMenuAutoOpened = (pathname) => {
  try {
    sessionStorage.setItem(`${MOBILE_MENU_AUTO_OPEN_STORAGE_PREFIX}${pathname}`, '1')
  } catch {
    // Ignore storage failures (private mode, blocked storage, etc.)
  }
}

const useAutoOpenMobileMenu = (setShowMobileMenu) => {
  const location = useLocation()

  useEffect(() => {
    const pathname = location.pathname
    if (!shouldAutoOpenMobileMenu(pathname)) return undefined

    const timeoutId = window.setTimeout(() => {
      if (window.innerWidth >= MOBILE_BREAKPOINT_PX) return
      setShowMobileMenu(true)
      markMobileMenuAutoOpened(pathname)
    }, 2000)

    return () => window.clearTimeout(timeoutId)
  }, [location.pathname, setShowMobileMenu])
}

const HamburgerIcon = ({ open = false }) => (
  <span className="relative block h-3.5 w-5" aria-hidden="true">
    <span
      className={`absolute left-0 top-0 block h-0.5 w-5 rounded-full bg-white transition-all duration-200 ${
        open ? 'translate-y-[6px] rotate-45' : ''
      }`}
    />
    <span
      className={`absolute left-0 top-[6px] block h-0.5 w-5 rounded-full bg-white transition-all duration-200 ${
        open ? 'opacity-0' : ''
      }`}
    />
    <span
      className={`absolute left-0 top-[12px] block h-0.5 w-5 rounded-full bg-white transition-all duration-200 ${
        open ? '-translate-y-[6px] -rotate-45' : ''
      }`}
    />
  </span>
)

const MobileMenuButton = ({ open, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand text-white shadow-modern transition-all duration-200 hover:bg-orange-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand md:hidden"
    aria-expanded={open}
    aria-controls="mobile-nav-menu"
    aria-label={label}
  >
    <HamburgerIcon open={open} />
  </button>
)

export const WelcomeNav = ({ onGetStarted }) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const mobileMenuRef = useRef(null)

  useAutoOpenMobileMenu(setShowMobileMenu)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false)
      }
    }

    if (showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMobileMenu])

  return (
    <div className="app-top-nav relative z-50" ref={mobileMenuRef}>
      {showMobileMenu && (
        <button
          type="button"
          aria-label="Close navigation menu"
          className={MOBILE_MENU_BACKDROP_CLASS}
          onClick={() => setShowMobileMenu(false)}
        />
      )}

      <nav className="relative z-[120] animate-fade-in border-b border-white/20 bg-white px-3 py-3 shadow-modern sm:px-4 md:px-8 md:py-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 sm:gap-4">
          {/* Logo Section */}
          <div className="flex min-w-0 items-center gap-2 sm:gap-3 md:gap-4">
            <div className="relative flex-shrink-0">
              <img
                className="h-10 w-10 rounded-xl shadow-modern transition-transform duration-300 hover:scale-105 sm:h-12 sm:w-12 md:h-14 md:w-14"
                src={logo}
                alt="Yummtime app logo"
              />
              {/* Subtle glow effect */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-orange-400/20 to-orange-600/20 opacity-0 transition-opacity duration-300 hover:opacity-100"></div>
            </div>

            <div className="min-w-0 flex flex-col">
              <h3 className="whitespace-nowrap text-lg font-bold tracking-tight text-orange-600 sm:text-xl md:text-2xl">
                It's Yummtime
              </h3>
              <p className="whitespace-nowrap text-[10px] font-medium leading-tight text-warm-gray-600 sm:text-xs">
                Discover Global Flavors
              </p>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden flex-shrink-0 items-center gap-1.5 sm:gap-3 md:flex md:gap-6">
            <Link
              to="/about"
              className="group relative whitespace-nowrap px-2 py-2 text-sm font-semibold text-warm-gray-700 transition-colors duration-300 hover:text-orange-600 sm:px-3 sm:text-base md:px-4"
            >
              About
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-orange-500 transition-all duration-300 group-hover:w-full"></span>
            </Link>

            {/* Enhanced CTA Button */}
            <div className="relative">
              <button
                type="button"
                onClick={onGetStarted}
                className="nav-get-started-lines inline-flex items-center gap-1.5 rounded-2xl border-0 bg-gradient-to-r from-orange-500 to-orange-600 px-3 py-2 text-sm font-bold text-white shadow-modern transition-all duration-300 hover:-translate-y-0.5 hover:from-orange-600 hover:to-orange-700 hover:shadow-glow sm:gap-2 sm:px-5 sm:py-2.5 sm:text-base md:px-6 md:py-3"
              >
                <span className="relative z-10 whitespace-nowrap">Get Started</span>
                <span className="relative z-10 text-base sm:text-xl">✨</span>
              </button>
            </div>
          </div>

          <MobileMenuButton
            open={showMobileMenu}
            onClick={() => setShowMobileMenu((open) => !open)}
            label={showMobileMenu ? 'Close navigation menu' : 'Open navigation menu'}
          />
        </div>

        {showMobileMenu && (
          <div id="mobile-nav-menu" className={MOBILE_MENU_PANEL_CLASS}>
            <div className="mx-auto flex max-w-7xl flex-col bg-white pt-1">
              <Link
                to="/about"
                onClick={() => setShowMobileMenu(false)}
                className={MOBILE_MENU_ITEM_CLASS}
              >
                About
              </Link>
              <button
                type="button"
                onClick={() => {
                  setShowMobileMenu(false)
                  onGetStarted()
                }}
                className={MOBILE_MENU_PRIMARY_CLASS}
              >
                <span>Get Started</span>
                <span aria-hidden="true">✨</span>
              </button>
            </div>
          </div>
        )}
      </nav>
    </div>
  )
}

// NAVIGATION COMPONENT: Functional component with authentication
export const Nav = () => {
  const { user, userProfile, billingCustomer, profileLoading, signOut, refreshUserProfile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [showPaymentIssueModal, setShowPaymentIssueModal] = useState(false)
  const [paymentIssueDismissed, setPaymentIssueDismissed] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [signOutError, setSignOutError] = useState('')
  const menuRef = useRef(null)
  const mobileMenuRef = useRef(null)

  useAutoOpenMobileMenu(setShowMobileMenu)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false)
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false)
      }
    }

    if (showUserMenu || showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu, showMobileMenu])

  useEffect(() => {
    if (!user) return

    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') !== 'success') return

    const syncProfile = async () => {
      await refreshUserProfile()
    }

    syncProfile()

    params.delete('checkout')
    const cleanedSearch = params.toString()
    const nextUrl = `${window.location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}${window.location.hash}`
    window.history.replaceState({}, '', nextUrl)
  }, [user, refreshUserProfile])

  useEffect(() => {
    if (!user) {
      setShowPaymentIssueModal(false)
      setPaymentIssueDismissed(false)
      return
    }

    const shouldShowPaymentIssue =
      userProfile?.tier === 'premium' &&
      !paymentIssueDismissed &&
      isPaymentIssueInGrace({
        subscriptionStatus: billingCustomer?.subscription_status,
        paymentGraceEndsAt: billingCustomer?.payment_grace_ends_at
      })

    setShowPaymentIssueModal(shouldShowPaymentIssue)
  }, [
    user,
    userProfile?.tier,
    billingCustomer?.subscription_status,
    billingCustomer?.payment_grace_ends_at,
    paymentIssueDismissed
  ])

  const closeMenus = () => {
    setShowUserMenu(false)
    setShowMobileMenu(false)
  }

  const handleSignOut = async () => {
    setSignOutError('')
    closeMenus()
    setShowSubscriptionModal(false)
    setShowUpgradeModal(false)
    setShowPaymentIssueModal(false)
    setPaymentIssueDismissed(false)
    setIsSigningOut(true)

    try {
      const { error } = await signOut()
      if (error) {
        console.error('Logout failed:', error)
        setSignOutError(error.message || 'Unable to log out. Please try again.')
        return
      }
      navigate('/', { replace: true })
    } finally {
      setIsSigningOut(false)
    }
  }

  const handleSubscriptionClick = () => {
    closeMenus()
    setShowSubscriptionModal(true)
  }

  const isPremium = userProfile?.tier === 'premium'
  const isFree = userProfile?.tier === 'free'
  const shouldShowSearchFlavorsLink = location.pathname !== '/'

  const handleSavedRecipesClick = async () => {
    const latestProfile = userProfile || await refreshUserProfile()

    if (latestProfile?.tier === 'free') {
      // Free tier - show upgrade modal
      closeMenus()
      setShowUpgradeModal(true)
    } else if (latestProfile?.tier === 'premium') {
      navigate('/saved-recipes')
      closeMenus()
    } else {
      closeMenus()
    }
  }

  return (
    <>
      {/* NAVIGATION BAR - TAILWIND CONVERTED WITH AUTH */}
      {/* Layout: Flex container, space between items, centered alignment  */}
      {/* Spacing: Proper margins, responsive design */}
      <div className="app-top-nav relative z-50" ref={mobileMenuRef}>
      {showMobileMenu && (
        <button
          type="button"
          aria-label="Close navigation menu"
          className={MOBILE_MENU_BACKDROP_CLASS}
          onClick={closeMenus}
        />
      )}

      <nav className="relative z-[120] my-0.5 px-4 text-white md:px-8">
        <div className="flex min-h-[8vh] items-center justify-between">
        {/* LEFT SECTION: Enhanced Logo and Title - Matching Landing Page */}
        <div className="mb-1 flex min-w-0 items-center space-x-3 md:space-x-4">
          <div className="relative flex-shrink-0">
            <img
              className="h-11 w-11 rounded-xl shadow-modern transition-transform duration-300 hover:scale-105 md:h-14 md:w-14"
              src={logo}
              alt="Yummtime app logo"
            />
            {/* Subtle glow effect */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-orange-400/20 to-orange-600/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
          </div>

          <div className="flex min-w-0 flex-col">
            <h3 className="truncate text-lg font-bold tracking-tight text-orange-600 sm:text-xl md:text-2xl">
              It's Yummtime
            </h3>
            <p className="hidden text-xs font-medium text-warm-gray-600 md:block">
              Discover Global Flavors
            </p>
          </div>
        </div>
        
        {/* DESKTOP RIGHT SECTION: Navigation Links and Auth */}
        <div className="hidden items-center space-x-4 md:flex md:space-x-6">
          {/* NAVIGATION LINKS - TAILWIND CONVERTED */}
          <ul className="flex list-none space-x-4 md:space-x-6">
            {/* HOME LINK - BRAND COLORS UPDATED */}
            {shouldShowSearchFlavorsLink && (
              <Link to="/" className="rounded px-3 py-2 text-brand transition-colors duration-200 hover:bg-white hover:text-black">
                <li className="font-medium">Search Flavors</li>
              </Link>
            )}
          </ul>
          
          {/* AUTHENTICATION SECTION */}
          {user ? (
            // AUTHENTICATED USER: Show user menu, upgrade (if free), and logout
            <div className="flex items-center space-x-3">
              <span className={NAV_PASSIVE_GREETING_CLASS}>
                Hello, there!
              </span>

              {/* USER MENU DROPDOWN */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  onMouseEnter={() => setShowUserMenu(true)}
                  className={NAV_SECONDARY_ACTION_CLASS}
                  aria-expanded={showUserMenu}
                  aria-haspopup="menu"
                >
                  <svg
                    className="h-5 w-5 text-white"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M12 12c2.76 0 5-2.46 5-5.5S14.76 1 12 1 7 3.46 7 6.5 9.24 12 12 12Z" />
                    <path d="M4 22c0-4.42 3.58-8 8-8s8 3.58 8 8H4Z" />
                  </svg>
                  <span>My Account</span>
                  <svg
                    className="h-3.5 w-3.5 text-white"
                    viewBox="0 0 12 8"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M6 8 0.8 0.5h10.4L6 8Z" />
                  </svg>
                </button>
                
                {/* DROPDOWN MENU */}
                {showUserMenu && (
                  <div 
                    className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-2 shadow-xl"
                    onMouseLeave={() => setShowUserMenu(false)}
                  >
                    {/* Subscription Management Option */}
                    <button
                      onClick={handleSubscriptionClick}
                      className="group w-full px-4 py-3 text-left transition-colors hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-700 group-hover:text-brand">
                        My Subscription
                      </span>
                    </button>
                    
                    {/* Saved Recipes Option */}
                    <button
                      onClick={handleSavedRecipesClick}
                      className="group flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 group-hover:text-brand">
                          Saved Recipes
                        </span>
                        {isFree && (
                          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                            PRO
                          </span>
                        )}
                      </div>
                    </button>
                    
                    {/* Divider */}
                    <div className="my-1 border-t border-gray-100"></div>
                    
                    {/* Account Info */}
                    <div className="px-4 py-2 text-xs text-gray-500">
                      {isPremium ? (
                        <span className="font-semibold text-yellow-600">⭐ Premium Account </span>
                      ) : isFree ? (
                        <span>Free Account</span>
                      ) : (
                        <span>{profileLoading ? 'Syncing account...' : 'Account sync unavailable'}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* UPGRADE BUTTON: Only show for confirmed free tier users */}
              {isFree && (
                <Button 
                  onClick={() => setShowUpgradeModal(true)}
                  variant="default"
                  size="sm"
                  className={NAV_UPGRADE_ACTION_CLASS}
                >
                  ⭐ Upgrade
                </Button>
              )}
              
              <Button 
                onClick={handleSignOut}
                variant="default"
                size="sm"
                disabled={isSigningOut}
                className={NAV_SECONDARY_ACTION_CLASS}
              >
                {isSigningOut ? 'Logging out...' : 'Logout'}
              </Button>
            </div>
          ) : (
            // UNAUTHENTICATED USER: Show login button
            <Button 
              onClick={() => setShowAuthModal(true)}
              variant="outline"
              size="sm"
              className="border-white text-white hover:bg-white hover:text-brand"
            >
              Login
            </Button>
          )}
        </div>

        {/* MOBILE: brand-orange hamburger with full action menu */}
        <MobileMenuButton
          open={showMobileMenu}
          onClick={() => setShowMobileMenu((open) => !open)}
          label={showMobileMenu ? 'Close navigation menu' : 'Open navigation menu'}
        />
        </div>

        {showMobileMenu && (
          <div id="mobile-nav-menu" className={MOBILE_MENU_PANEL_CLASS}>
            <div className="flex flex-col bg-white">
              {user ? (
                <>
                  <div className="border-b border-gray-100 bg-white px-4 py-3">
                    <p className="text-sm font-semibold text-brand">Hello, there!</p>
                    <p className="mt-0.5 text-xs text-warm-gray-600">
                      {isPremium
                        ? '⭐ Premium Account'
                        : isFree
                          ? 'Free Account'
                          : profileLoading
                            ? 'Syncing account...'
                            : 'Account sync unavailable'}
                    </p>
                  </div>

                  {shouldShowSearchFlavorsLink && (
                    <Link
                      to="/"
                      onClick={closeMenus}
                      className={MOBILE_MENU_ITEM_CLASS}
                    >
                      Search Flavors
                    </Link>
                  )}

                  <button
                    type="button"
                    onClick={handleSubscriptionClick}
                    className={MOBILE_MENU_ITEM_CLASS}
                  >
                    My Subscription
                  </button>

                  <button
                    type="button"
                    onClick={handleSavedRecipesClick}
                    className={MOBILE_MENU_ITEM_CLASS}
                  >
                    <span>Saved Recipes</span>
                    {isFree && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                        PRO
                      </span>
                    )}
                  </button>

                  {isFree && (
                    <button
                      type="button"
                      onClick={() => {
                        closeMenus()
                        setShowUpgradeModal(true)
                      }}
                      className="mx-4 mt-2.5 flex w-[calc(100%-2rem)] items-center justify-center gap-1.5 rounded-full border border-yellow-400 bg-yellow-400 px-4 py-2.5 text-sm font-semibold text-yellow-900 transition-colors duration-200 hover:bg-yellow-300"
                    >
                      ⭐ Upgrade
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleSignOut}
                    disabled={isSigningOut}
                    className={`${MOBILE_MENU_PRIMARY_CLASS} disabled:cursor-not-allowed disabled:opacity-70`}
                  >
                    {isSigningOut ? 'Logging out...' : 'Logout'}
                  </button>
                </>
              ) : (
                <>
                  {shouldShowSearchFlavorsLink && (
                    <Link
                      to="/"
                      onClick={closeMenus}
                      className={MOBILE_MENU_ITEM_CLASS}
                    >
                      Search Flavors
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      closeMenus()
                      setShowAuthModal(true)
                    }}
                    className={MOBILE_MENU_PRIMARY_CLASS}
                  >
                    Login
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
      </div>

      {signOutError && (
        <div className="px-4 md:px-8 pb-2">
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {signOutError}
          </div>
        </div>
      )}

      {/* AUTHENTICATION MODAL */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
      
      {/* TIER UPGRADE MODAL */}
      <TierUpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
      
      {/* SUBSCRIPTION MANAGEMENT MODAL */}
      <SubscriptionManagementModal 
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onUpgrade={() => setShowUpgradeModal(true)}
      />

      {/* PAYMENT FAILURE GRACE MODAL */}
      <PaymentIssueModal
        isOpen={showPaymentIssueModal}
        onClose={() => {
          setPaymentIssueDismissed(true)
          setShowPaymentIssueModal(false)
        }}
      />
    </>
  )
}
